import Axios, { AxiosError, RawAxiosRequestHeaders } from 'axios'
import Response from '@/hexapla-core/client/response'

type Headers = RawAxiosRequestHeaders

const axiosInstance = Axios.create()

// On the login call a 401 only means wrong credentials, not a lost session.
const isLoginRequest = (url?: string): boolean => !!url && /\/api\/login(\?|$)/.test(url)

/**
 * `onUnauthorized` runs for any 401 except the login call. Anywhere else a 401
 * means the session is gone — expired, or replaced by a sign-in elsewhere — and
 * the app should return to the login screen rather than fail call by call. The
 * error is still rejected so each caller's own catch block runs.
 */
export function configureClient(getToken: () => string, onUnauthorized?: () => void) {
  axiosInstance.interceptors.request.use((config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (onUnauthorized && error.response?.status === 401 && !isLoginRequest(error.config?.url)) {
        onUnauthorized()
      }
      return Promise.reject(error)
    }
  )
}

// Module Interface
export interface Client {
  get: <Res>(url: string, headers?: Headers) => Promise<Res>
  post: <Req, Res>(url: string, body: Req, headers?: Headers) => Promise<Res>
  postFormData: <Res>(url: string, formData: FormData, headers?: Headers) => Promise<Res>
  patch: <Req, Res>(url: string, body: Req, headers?: Headers) => Promise<Res>
  put: <Req, Res>(url: string, body: Req, headers?: Headers) => Promise<Res>
  destroy: <Res>(url: string, headers?: Headers) => Promise<Res>
}

export const get = async <Res>(url: string, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(axiosInstance.get(url, { headers }))

export const post = async <Req, Res>(url: string, body: Req, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(axiosInstance.post(url, body, { headers }))

export const postFormData = async <Res>(url: string, formData: FormData, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(
    axiosInstance.post(url, formData, {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
    })
  )

export const patch = async <Req, Res>(url: string, body: Req, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(axiosInstance.patch(url, body, { headers }))

export const put = async <Req, Res>(url: string, body: Req, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(axiosInstance.put(url, body, { headers }))

export const destroy = async <Res>(url: string, headers?: Headers): Promise<Res> =>
  await Response.fromPromise<Res>(axiosInstance.delete(url, { headers }))

export default {
  get,
  post,
  postFormData,
  patch,
  put,
  destroy,
} as Client
